import data from "data/live/navigation";

// Helper function to filter out invalid values that could cause NaN in calculations
function filterValidNumbers(arr) {
    return arr.filter(val => 
        val !== null && 
        val !== undefined && 
        val !== 'N/A' && 
        !isNaN(val) && 
        typeof val === 'number'
    );
}

// Helper function to safely calculate mean from an array, filtering out invalid values
function safeCalculateMean(arr) {
    const validValues = filterValidNumbers(arr);
    return validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : null;
}

// Helper function to safely calculate variance, filtering out invalid values
function safeCalculateVariance(arr, mean = null) {
    const validValues = filterValidNumbers(arr);
    if (validValues.length === 0) return null;
    
    const calculatedMean = mean !== null ? mean : safeCalculateMean(validValues);
    if (calculatedMean === null) return null;
    
    return validValues.reduce((sum, val) => sum + (val - calculatedMean) ** 2, 0) / validValues.length;
}

// Helper function to convert 0-10 scale back to original Likert scale
function convertToLikertScale(score, likertSize) {
    // Convert 0-10 score back to 1-likertSize scale
    // Original conversion: 1->1.66, 2->3.33, 3->5, 4->6.66, 5->8.33, 6->10
    // So we need to reverse this mapping
    const step = 10 / likertSize;
    const likertValue = Math.round(score / step);
    return Math.max(1, Math.min(likertSize, likertValue || 1));
}

// Helper function to create distribution array based on scale type
function createDistribution(scores, questionStructure) {
    const validScores = filterValidNumbers(scores);
    if (validScores.length === 0) return null;

    if (questionStructure && questionStructure.useLikert) {
        console.log(questionStructure.useLikert);
        // Create Likert scale distribution (1-based indexing)
        const likertSize = questionStructure.useLikert;
        const distribution = Array(likertSize).fill(0);
        
        for (const score of validScores) {
            const likertValue = convertToLikertScale(score, likertSize);
            if (likertValue >= 1 && likertValue <= likertSize) {
                distribution[likertValue - 1] += 1; // Convert to 0-based index
            }
        }
        console.log(distribution);

        return distribution;
    } else {
        // Create standard 0-10 distribution
        const distribution = Array(11).fill(0);
        for (const score of validScores) {
            if (score >= 0 && score <= 10) {
                distribution[Math.round(score)] += 1;
            }
        }
        return distribution;
    }
}

export function calculate_summary_chart(data, lastData, anchor, selectedFactor, core_data, rule) {

    if (!anchor || !selectedFactor) {
        return null;
    }

    // Find the anchor category in the categories array
    const categories = core_data.categories[0].categories;
    const anchorVariable = anchor;
    const questionStructure = core_data.questions;

    // Find the anchor category in the categories array
    const anchorCategory = categories.find(cat => cat.id === anchorVariable);

    if (!anchorCategory) {
        throw new Error(`Anchor category with id ${anchorVariable} not found.`);
    }

    // Map category option IDs to names
    const categoryOptions = {};
    for (const option of anchorCategory.options) {
        categoryOptions[option.id] = option.name;
    }

    // HACK: Remap invalid GUIDs to the correct GUID for this customer
    const invalidIds = [
        "67fcb2a9-87f9-457f-9e48-5553110d5c85",
        "c504c82b-fa4c-4426-9392-dd10aa6410ba"
    ];
    const correctId = "fea12aab-0b6d-4604-b0dd-537e5bff8727";


    invalidIds.forEach((invalidId) => {
        if (categoryOptions.hasOwnProperty(invalidId)) {

            // Copy the name from the invalid key to the correct key
            categoryOptions[correctId] = categoryOptions[invalidId];
            // Remove the invalid key
            delete categoryOptions[invalidId];
        }
    });

    // Initialize an object to hold data grouped by category options
    const groupedData = {};
    const questionGroupedData = {}; // Holds data per question
    // Initialize similar structures for lastData
    const lastGroupedData = {};
    const lastQuestionGroupedData = {};

    for (const optionId in categoryOptions) {
        const categoryName = categoryOptions[optionId];
        groupedData[categoryName] = [];
        questionGroupedData[categoryName] = {}; // Initialize per category

        // Initialize for lastData
        lastGroupedData[categoryName] = [];
        lastQuestionGroupedData[categoryName] = {};
    }

    // Find the dimension ID for the selected factor in the questionStructure
    let selectedDimensionId = selectedFactor.dimension;

    if (selectedDimensionId === null) {
        throw new Error(`Selected factor with id ${selectedFactor.id} not found in questionStructure.`);
    }

    // Build a map from question indices to their properties (reverse flag, question text)
    const questionMap = {};
    selectedFactor.questions.forEach((qObj, index) => {
        questionMap[index] = {
            reverse: qObj.reverse,
            text: qObj.q
        };
    });

    // Initialize arrays to collect overall data
    const overallScores = []; // Collects the average scores of the factor across all responses
    const questionOverallScores = {}; // Collects responses per question across all categories

    // Similar arrays for lastData
    const lastOverallScores = [];
    const lastQuestionOverallScores = {};
   
    // Helper function to process data
    function processData(
      dataset,
      groupedDataObj,
      questionGroupedDataObj,
      overallScoresArr,
      questionOverallScoresObj
    ) {
      for (const response of dataset) {
        // Find the value of the anchor variable (category option)
        const categoryResponse = response?.categories?.find(
          (cat) => cat.id === anchorVariable
        );
        if (!categoryResponse || !categoryResponse.response) {
          // If no response for the anchor category, skip this response
          continue;
        }
          let categoryOptionId = categoryResponse.response;
          // If this ID was invalid, remap to the correct one
          if (invalidIds.includes(categoryOptionId)) {
              categoryOptionId = correctId;
          }
          const categoryOptionName = categoryOptions[categoryOptionId];
        if (!categoryOptionName) {
          // If the option ID is not found, skip this response
          continue;
        }

        // Extract responses to selected factor's questions
        const responses = [];
        const questionResponses = {}; // Holds response per question
        for (const question of response.questions) {
          // Check if the question belongs to the selected factor by matching dimension ID, factor ID, and question index
          if (
            question.id == selectedDimensionId && // dimension ID
            question.factor == selectedFactor.id - 1 && // factor ID
            questionMap.hasOwnProperty(question.q) // question.q is index in selectedFactor.questions
          ) {
            const qIndex = question.q;
            let resp = question.response;
            if (resp !== null && resp !== undefined && resp !== 'N/A' && !isNaN(resp) && typeof resp === 'number') {
              // Adjust for reverse-scored questions
              const reverse = questionMap[qIndex].reverse;
              if (reverse) {
                resp = 10 - resp; // Assuming responses are from 1 to 10
              }
              responses.push(resp);

              // Store response per question
              const questionText = questionMap[qIndex].text;
              if (!questionResponses[questionText]) {
                questionResponses[questionText] = [];
              }
              questionResponses[questionText].push(resp);

              // Collect overall responses per question
              if (!questionOverallScoresObj[questionText]) {
                questionOverallScoresObj[questionText] = [];
              }
              questionOverallScoresObj[questionText].push(resp);
            }
          }
        }

        if (responses.length > 0) {
          // Compute the average score for this response (across the selected factor's questions)
          const avgScore = safeCalculateMean(responses);
          if (avgScore !== null) {
            // Add the score to the list for the corresponding category option
            groupedDataObj[categoryOptionName].push(avgScore);

            // Add valid responses to overallScores
            const validResponses = filterValidNumbers(responses);
            overallScoresArr.push(...validResponses);
          }

          // Store per-question responses
          for (const [questionText, resps] of Object.entries(
            questionResponses
          )) {
            if (!questionGroupedDataObj[categoryOptionName][questionText]) {
              questionGroupedDataObj[categoryOptionName][questionText] = [];
            }
            // Since we're processing one response at a time, resps is an array with one element
            questionGroupedDataObj[categoryOptionName][questionText].push(
              resps[0]
            );
          }
        }
      }
    }
    // Process current data
    processData(data, groupedData, questionGroupedData, overallScores, questionOverallScores);

    // Process lastData if provided
    if (lastData) {
        processData(lastData, lastGroupedData, lastQuestionGroupedData, lastOverallScores, lastQuestionOverallScores);
    }

    // Now compute the counts for each category
    const categoryCounts = {};
    for (const categoryOptionName in groupedData) {
        categoryCounts[categoryOptionName] = groupedData[categoryOptionName].length;
    }

    // Filter out categories that do not meet the rule
    const categoriesToKeep = Object.keys(categoryCounts).filter(
        categoryOptionName => categoryCounts[categoryOptionName] > rule
    );

    // Now compute the statistics for each category option that meets the rule
    const output = {
        categories: categoriesToKeep, // Only include categories that meet the rule
        series: [
            {
                name: 'Average Score',
                data: []
            },
            {
                name: 'Max Score',
                data: []
            },
            {
                name: 'Min Score',
                data: []
            },
            {
                name: 'Standard Deviation',
                data: []
            },
            {
                name: 'Change',
                data: []
            }
        ],
        questionSeries: {}, // Holds data per question
        overallStats: {}, // Holds overall average and standard deviation for the factor
        questionOverallStats: {}, // Holds overall average and standard deviation per question
        // New fields for last average scores
        lastSeries: [
            {
                name: 'Last Average Score',
                data: []
            }
        ],
        lastOverallStats: {}, // Holds overall average and standard deviation for lastData
        questionLastSeries: {}, // Holds data per question for lastData
        questionLastOverallStats: {} // Holds overall average and standard deviation per question for lastData
    };

    // Compute overall statistics for the factor
    const validOverallScores = filterValidNumbers(overallScores);
    if (validOverallScores.length > 0) {
        const overallMean = safeCalculateMean(validOverallScores);
        const overallVariance = safeCalculateVariance(validOverallScores, overallMean);
        const overallStd = overallVariance !== null ? Math.sqrt(overallVariance) : null;

        // Add to output
        output.overallStats = {
            average: overallMean,
            standardDeviation: overallStd
        };
    } else {
        output.overallStats = {
            average: null,
            standardDeviation: null
        };
    }

    // Compute overall statistics for lastData
    const validLastOverallScores = filterValidNumbers(lastOverallScores);
    if (validLastOverallScores.length > 0) {
        const lastOverallMean = safeCalculateMean(validLastOverallScores);
        const lastOverallVariance = safeCalculateVariance(validLastOverallScores, lastOverallMean);
        const lastOverallStd = lastOverallVariance !== null ? Math.sqrt(lastOverallVariance) : null;

        // Add to output
        output.lastOverallStats = {
            average: lastOverallMean,
            standardDeviation: lastOverallStd
        };
    } else {
        output.lastOverallStats = {
            average: null,
            standardDeviation: null
        };
    }

    // Compute statistics per category option
    for (const categoryOptionName of categoriesToKeep) {
        const scores = groupedData[categoryOptionName];
        const lastScores = lastGroupedData[categoryOptionName] || [];

        // Current data statistics
        const validScores = filterValidNumbers(scores);
        if (validScores.length > 0) {
            const mean = safeCalculateMean(validScores);
            const max = Math.max(...validScores);
            const min = Math.min(...validScores);
            const variance = safeCalculateVariance(validScores, mean);
            const std = variance !== null ? Math.sqrt(variance) : null;

            output.series[0].data.push(mean);
            output.series[1].data.push(max);
            output.series[2].data.push(min);
            output.series[3].data.push(std);

            // Calculate change as difference in 0–100 scale if last data meets the rule threshold
            const validLastScores = filterValidNumbers(lastScores);
            if (validLastScores.length > rule) {
                const lastMean = safeCalculateMean(validLastScores);
                if (mean !== null && lastMean !== null) {
                    // Scale to 0–100 and take the absolute difference in points
                    const change = (mean - lastMean) * 10;
                    output.series[4].data.push(change);
                } else {
                    output.series[4].data.push(null);
                }
            } else {
                output.series[4].data.push(null);
            }
        } else {
            output.series[0].data.push(null);
            output.series[1].data.push(null);
            output.series[2].data.push(null);
            output.series[3].data.push(null);
            output.series[4].data.push(null);
        }

        // Last data statistics
        const validLastScores = filterValidNumbers(lastScores);
        if (validLastScores.length > 0) {
            const lastMean = safeCalculateMean(validLastScores);
            output.lastSeries[0].data.push(lastMean);
        } else {
            output.lastSeries[0].data.push(null);
        }
    }

    // Compute statistics per question
    const questionTexts = Object.values(questionMap).map(q => q.text);

    for (const questionText of questionTexts) {
        output.questionSeries[questionText] = {
            'Average Score': { name: 'Average Score', data: [] },
            'Max Score': { name: 'Max Score', data: [] },
            'Min Score': { name: 'Min Score', data: [] },
            'Standard Deviation': { name: 'Standard Deviation', data: [] }
        };

        // Initialize lastQuestionSeries
        output.questionLastSeries[questionText] = {
            'Last Average Score': { name: 'Last Average Score', data: [] }
        };

        for (const categoryOptionName of categoriesToKeep) {
            const scores = questionGroupedData[categoryOptionName][questionText] || [];
            const lastScores = lastQuestionGroupedData[categoryOptionName][questionText] || [];

            // Current data statistics
            if (scores.length > 0) {
                const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
                const max = Math.max(...scores);
                const min = Math.min(...scores);
                const variance = scores.reduce((sum, val) => sum + (val - mean) ** 2, 0) / scores.length;
                const std = Math.sqrt(variance);

                output.questionSeries[questionText]['Average Score'].data.push(mean);
                output.questionSeries[questionText]['Max Score'].data.push(max);
                output.questionSeries[questionText]['Min Score'].data.push(min);
                output.questionSeries[questionText]['Standard Deviation'].data.push(std);
            } else {
                output.questionSeries[questionText]['Average Score'].data.push(null);
                output.questionSeries[questionText]['Max Score'].data.push(null);
                output.questionSeries[questionText]['Min Score'].data.push(null);
                output.questionSeries[questionText]['Standard Deviation'].data.push(null);
            }

            // Last data statistics per question
            if (lastScores.length > 0) {
                const lastMean = lastScores.reduce((a, b) => a + b, 0) / lastScores.length;
                output.questionLastSeries[questionText]['Last Average Score'].data.push(lastMean);
            } else {
                output.questionLastSeries[questionText]['Last Average Score'].data.push(null);
            }
        }

        // Compute overall statistics per question across all categories
        const overallQuestionScores = questionOverallScores[questionText] || [];
        if (overallQuestionScores.length > 0) {
            const mean = overallQuestionScores.reduce((a, b) => a + b, 0) / overallQuestionScores.length;
            const variance =
                overallQuestionScores.reduce((sum, val) => sum + (val - mean) ** 2, 0) /
                overallQuestionScores.length;
            const std = Math.sqrt(variance);

            output.questionOverallStats[questionText] = {
                average: mean,
                standardDeviation: std
            };
        } else {
            output.questionOverallStats[questionText] = {
                average: null,
                standardDeviation: null
            };
        }

        // Compute overall statistics per question for lastData
        const lastOverallQuestionScores = lastQuestionOverallScores[questionText] || [];
        if (lastOverallQuestionScores.length > 0) {
            const lastMean = lastOverallQuestionScores.reduce((a, b) => a + b, 0) / lastOverallQuestionScores.length;
            const lastVariance =
                lastOverallQuestionScores.reduce((sum, val) => sum + (val - lastMean) ** 2, 0) /
                lastOverallQuestionScores.length;
            const lastStd = Math.sqrt(lastVariance);

            output.questionLastOverallStats[questionText] = {
                average: lastMean,
                standardDeviation: lastStd
            };
        } else {
            output.questionLastOverallStats[questionText] = {
                average: null,
                standardDeviation: null
            };
        }
    }

    return output;
}

/* -------------------------------------------------------------
   Utility: text normaliser
   ------------------------------------------------------------- */
   function normalise(str) {
    if (typeof str !== 'string') return '';
    return str
      .toLowerCase()
      .replace(/\{.*?}/g, '')      // strip {entity} placeholder
      .replace(/\u2026/g, '...')   // … → ...
      .replace(/[.\s]+$/, '')      // trailing dots / spaces
      .replace(/\s+/g, ' ')        // collapse whitespace
      .trim();
  }
  
  /* -------------------------------------------------------------
     Main
     ------------------------------------------------------------- */
  export function calculate_outcome_summary_chart(
    data,
    lastData,
    anchor,
    selectedFactor,  // ← outcome *section* the user picked (e.g. { id:'3', name:'Engagement' })
    core_data,
    outcomeQ,        // ← full outcome definition
    rule
  ) {
    /* ---------- 1. guard clauses ---------- */
    if (!anchor || !selectedFactor) return null;
  
    /* ---------- 2. basic setup (unchanged) ---------- */
    const categories          = core_data.categories[0].categories;
    const anchorVariable      = anchor;
    const anchorCategory      = categories.find(c => c.id === anchorVariable);
    if (!anchorCategory) throw new Error(`Anchor category with id ${anchorVariable} not found.`);
  
    /* map option id → name */
    const categoryOptions = Object.fromEntries(
      anchorCategory.options.map(o => [o.id, o.name])
    );
  
    /* initialise grouping objects */
    const groupedData             = {};
    const questionGroupedData     = {};
    const lastGroupedData         = {};
    const lastQuestionGroupedData = {};
    for (const optionId in categoryOptions) {
      const name                              = categoryOptions[optionId];
      groupedData[name]                       = [];
      questionGroupedData[name]               = {};
      lastGroupedData[name]                   = [];
      lastQuestionGroupedData[name]           = {};
    }
  
    /* ---------- 3. build look-ups for the chosen outcome section ---------- */
    const questionMap         = {};           // normalised text → { text }
    const selectedQuestionSet = new Set();
  
    outcomeQ.questions               // outcomeQ has shape [{ id:'1', name:'…', questions:[…] }, …]
      .filter(sec => String(sec.id) === String(selectedFactor.id))
      .forEach(sec => {
        sec.questions.forEach(qObj => {
          const norm = normalise(qObj.question);   // English only
          selectedQuestionSet.add(norm);
          questionMap[norm] = { text: qObj.question };
        });
      });
  
    /* ---------- 4. containers for overall statistics ---------- */
    const overallScores            = [];
    const questionOverallScores    = {};
    const lastOverallScores        = [];
    const lastQuestionOverallScores = {};
  
    /* ---------- 5. helper to crunch one dataset (current or last) ---------- */
    function processData(
      dataset,
      groupedDataObj,
      questionGroupedDataObj,
      overallScoresArr,
      questionOverallScoresObj
    ) {
      for (const resp of dataset) {
        /* anchor category lookup */
        const catResp = resp.categories.find(c => c.id === anchorVariable);
        if (!catResp || !catResp.response) continue;
  
        const optionName = categoryOptions[catResp.response];
        if (!optionName) continue;
  
        /* pull answers that belong to THIS outcome section - DIRECT AGGREGATION */
        const eo = resp.employee_outcomes;
        if (eo && eo.responses) {
          eo.responses.forEach(q => {
            const normText = normalise(q.question);
            if (!selectedQuestionSet.has(normText)) return;
  
            const val = q.response;
            if (val == null || val === 'N/A' || isNaN(val)) return;
  
            /* store raw values directly for direct aggregation */
            groupedDataObj[optionName].push(val);
            overallScoresArr.push(val);
  
            const qText = questionMap[normText].text;
            (questionGroupedDataObj[optionName][qText] = 
              questionGroupedDataObj[optionName][qText] || []).push(val);
            (questionOverallScoresObj[qText] =
              questionOverallScoresObj[qText] || []).push(val);
          });
        }
      }
    }
  
    /* ---------- 6. run for current & last datasets ---------- */
    processData(data,      groupedData,     questionGroupedData,     overallScores,     questionOverallScores);
    if (lastData)
      processData(lastData, lastGroupedData, lastQuestionGroupedData, lastOverallScores, lastQuestionOverallScores);
  
    /* ---------- 7. downstream statistics code (aligned with heatmap method) ---------- */

    /* tiny helper for direct average calculation like heatmap */
    function calcStats(arr) {
      if (!arr.length) return { average: null, standardDeviation: null };
      const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
      const std  = Math.sqrt(arr.reduce((s, v) => s + (v - mean) ** 2, 0) / arr.length);
      return { average: mean, standardDeviation: std };
    }
  
    /* 7a. keep only categories > rule */
    const categoryCounts  = Object.fromEntries(
      Object.entries(groupedData).map(([k, v]) => [k, v.length])
    );
    const categoriesToKeep = Object.keys(categoryCounts).filter(k => categoryCounts[k] > rule);
  
    /* 7b. build output skeleton */
    const output = {
      categories: categoriesToKeep,
      series: [
        { name: 'Average Score', data: [] },
        { name: 'Max Score',     data: [] },
        { name: 'Min Score',     data: [] },
        { name: 'Standard Deviation', data: [] },
        { name: 'Change', data: [] }
      ],
      questionSeries:           {},
      overallStats:             calcStats(overallScores),
      questionOverallStats:     {},
      lastSeries:               [{ name: 'Last Average Score', data: [] }],
      lastOverallStats:         calcStats(lastOverallScores),
      questionLastSeries:       {},
      questionLastOverallStats: {}
    };
  
    /* 7c. per-category stats */
    categoriesToKeep.forEach(cat => {
      const cur = groupedData[cat];
      const prv = lastGroupedData[cat] || [];
  
      if (cur.length) {
        const curStats = calcStats(cur);
        output.series[0].data.push(curStats.average);
        output.series[1].data.push(Math.max(...cur));
        output.series[2].data.push(Math.min(...cur));
        output.series[3].data.push(curStats.standardDeviation);
  
        if (prv.length) {
          const prvAvg = calcStats(prv).average;
          // Calculate change as difference in 0–100 scale (multiply by 10 for percentage points)
          const change = (curStats.average - prvAvg) * 10;
          output.series[4].data.push(change);
        } else {
          output.series[4].data.push(null);
        }
      } else {
        output.series.forEach(s => s.data.push(null));
      }
  
      output.lastSeries[0].data.push(prv.length ? calcStats(prv).average : null);
    });
  
    /* 7d. per-question stats */
    const allQTexts = Object.values(questionMap).map(q => q.text);
    allQTexts.forEach(qText => {
      output.questionSeries[qText] = {
        'Average Score':        { name: 'Average Score', data: [] },
        'Max Score':            { name: 'Max Score',     data: [] },
        'Min Score':            { name: 'Min Score',     data: [] },
        'Standard Deviation':   { name: 'Standard Deviation', data: [] }
      };
      output.questionLastSeries[qText] = {
        'Last Average Score':   { name: 'Last Average Score', data: [] }
      };
  
      categoriesToKeep.forEach(cat => {
        const curQ = questionGroupedData[cat][qText] || [];
        const prvQ = lastQuestionGroupedData[cat][qText] || [];
  
        /* current stats */
        if (curQ.length) {
          const curStats = calcStats(curQ);
          output.questionSeries[qText]['Average Score'].data.push(curStats.average);
          output.questionSeries[qText]['Max Score'].data.push(Math.max(...curQ));
          output.questionSeries[qText]['Min Score'].data.push(Math.min(...curQ));
          output.questionSeries[qText]['Standard Deviation'].data.push(curStats.standardDeviation);
        } else {
          Object.values(output.questionSeries[qText]).forEach(s => s.data.push(null));
        }
  
        /* last stats */
        output.questionLastSeries[qText]['Last Average Score'].data
          .push(prvQ.length ? calcStats(prvQ).average : null);
      });
  
      /* overall per-question */
      output.questionOverallStats[qText]     = calcStats(questionOverallScores[qText]     || []);
      output.questionLastOverallStats[qText] = calcStats(lastQuestionOverallScores[qText] || []);
    });
  
    return output;
  }
  


export function calculate_longitudinal_chart(timeSeriesData, anchorVariable, selectedFactor, categories, questionStructure) {
    // Map to store aggregated data
    const aggregatedData = {};

    // Get category options
    const anchorCategory = categories.find(cat => cat.id === anchorVariable);
    if (!anchorCategory) {
        throw new Error(`Anchor category with id ${anchorVariable} not found.`);
    }
    const categoryOptions = {};
    for (const option of anchorCategory.options) {
        categoryOptions[option.id] = option.name;
    }
    const categoryNames = Object.values(categoryOptions);

    // Find the dimension ID for the selected factor in the questionStructure
    let selectedDimensionId = selectedFactor.dimension;


    if (selectedDimensionId === null) {
        throw new Error(`Selected factor with id ${selectedFactor.id} not found in questionStructure.`);
    }

    // Build question map (similar to previous)
    const questionMap = {};
    selectedFactor.questions.forEach((qObj, index) => {
        questionMap[index] = {
            reverse: qObj.reverse,
            text: qObj.q
        };
    });

    // Initialize aggregated data structure
    for (const categoryName of categoryNames) {
        aggregatedData[categoryName] = {};
    }

    // Helper function to parse date strings like 'Q1 2024' into comparable values
    function parseDateString(dateStr) {
        const [quarterStr, yearStr] = dateStr.split(' ');
        const quarter = parseInt(quarterStr.replace('Q', ''));
        const year = parseInt(yearStr);
        return { year, quarter };
    }

    // Collect all dates and sort them from oldest to newest
    const allDatesSet = new Set(timeSeriesData.map(tp => tp.date));
    const allDates = Array.from(allDatesSet);
    allDates.sort((a, b) => {
        const dateA = parseDateString(a);
        const dateB = parseDateString(b);
        return dateA.year !== dateB.year ? dateA.year - dateB.year : dateA.quarter - dateB.quarter;
    });

    // Process each time point
    for (const timePoint of timeSeriesData) {
        const { date, responses } = timePoint;

        // Prepare data structures for this date
        for (const categoryName of categoryNames) {
            if (!aggregatedData[categoryName][date]) {
                aggregatedData[categoryName][date] = {
                    responses: [],
                    questionResponses: {}
                };
            }
        }
        // Process responses for this time point
        for (const responseEMP of responses) {
            const response = responseEMP.response;
            // Find the value of the anchor variable (category option)
            const categoryResponse = response.categories.find(cat => cat.id === anchorVariable);
            if (!categoryResponse || categoryResponse.response === null || categoryResponse.response === undefined) {
                continue; // Skip if no category response
            }
            const categoryOptionId = categoryResponse.response;
            const categoryOptionName = categoryOptions[categoryOptionId];
            if (!categoryOptionName) {
                continue; // Skip if invalid category option
            }

            const dataEntry = aggregatedData[categoryOptionName][date];

            // Extract responses to selected factor's questions
            const responsesArray = [];
            const questionResponses = {}; // Holds response per question
            for (const question of response.questions) {
                if (
                    question.id == selectedDimensionId && // dimension ID
                    question.factor == selectedFactor.id-1 && // factor ID
                    questionMap.hasOwnProperty(question.q) // question.q is index in selectedFactor.questions
                ) {
                    const qIndex = question.q;
                    let resp = question.response;
                    if (resp !== null && resp !== undefined && resp !== 'N/A' && !isNaN(resp) && typeof resp === 'number') {
                        // Adjust for reverse-scored questions
                        const reverse = questionMap[qIndex].reverse;
                        if (reverse) {
                            resp = 10 - resp; // Assuming responses are from 1 to 10
                        }
                        responsesArray.push(resp);

                        // Store response per question
                        const questionText = questionMap[qIndex].text;
                        if (!questionResponses[questionText]) {
                            questionResponses[questionText] = [];
                        }
                        questionResponses[questionText].push(resp);
                    }
                }
            }

            if (responsesArray.length > 0) {
                // Add to aggregated data
                dataEntry.responses.push(...responsesArray);
                for (const [questionText, resps] of Object.entries(questionResponses)) {
                    if (!dataEntry.questionResponses[questionText]) {
                        dataEntry.questionResponses[questionText] = [];
                    }
                    dataEntry.questionResponses[questionText].push(...resps);
                }
            }
        }
    }

    // Now compute statistics for each category option over time
    const output = {
        series: {},         // Contains overall statistics per category
        questionSeries: {}  // Contains per-question statistics per category
    };

    // Initialize series for each category and statistical measure
    for (const categoryName of categoryNames) {
        output.series[categoryName] = {
            'Average Score': [],
            'Max Score': [],
            'Min Score': [],
            'Standard Deviation': [],
            'Distribution': [] // Added distribution data
        };
        output.questionSeries[categoryName] = {};
        for (const qObj of selectedFactor.questions) {
            const questionText = qObj.q;
            output.questionSeries[categoryName][questionText] = {
                'Average Score': [],
                'Max Score': [],
                'Min Score': [],
                'Standard Deviation': [],
                'Distribution': [] // Added distribution data
            };
        }
    }

    // Process data and populate output
    for (const date of allDates) {
        for (const categoryName of categoryNames) {
            const dataEntry = aggregatedData[categoryName][date];

            // Process overall statistics
            const stats = output.series[categoryName];
            if (dataEntry && dataEntry.responses.length > 0) {
                const scores = dataEntry.responses;
                const validScores = filterValidNumbers(scores);
                if (validScores.length > 0) {
                    const mean = safeCalculateMean(validScores);
                    const max = Math.max(...validScores);
                    const min = Math.min(...validScores);
                    const variance = safeCalculateVariance(validScores, mean);
                    const std = variance !== null ? Math.sqrt(variance) : null;

                    stats['Average Score'].push({ x: date, y: mean });
                    stats['Max Score'].push({ x: date, y: max });
                    stats['Min Score'].push({ x: date, y: min });
                    stats['Standard Deviation'].push({ x: date, y: std });

                    // Compute distribution using helper function
                    const distribution = createDistribution(validScores, questionStructure);
                    stats['Distribution'].push({ x: date, y: distribution });
                } else {
                    // No valid data for this date
                    stats['Average Score'].push({ x: date, y: null });
                    stats['Max Score'].push({ x: date, y: null });
                    stats['Min Score'].push({ x: date, y: null });
                    stats['Standard Deviation'].push({ x: date, y: null });
                    stats['Distribution'].push({ x: date, y: null });
                }
            } else {
                // No data for this date
                stats['Average Score'].push({ x: date, y: null });
                stats['Max Score'].push({ x: date, y: null });
                stats['Min Score'].push({ x: date, y: null });
                stats['Standard Deviation'].push({ x: date, y: null });
                stats['Distribution'].push({ x: date, y: null });
            }

            // Process per-question statistics
            for (const qObj of selectedFactor.questions) {
                const questionText = qObj.q;
                const qStats = output.questionSeries[categoryName][questionText];
                if (dataEntry && dataEntry.questionResponses[questionText]) {
                    const qScores = dataEntry.questionResponses[questionText];
                    const validQScores = filterValidNumbers(qScores);
                    if (validQScores.length > 0) {
                        const qMean = safeCalculateMean(validQScores);
                        const qMax = Math.max(...validQScores);
                        const qMin = Math.min(...validQScores);
                        const qVariance = safeCalculateVariance(validQScores, qMean);
                        const qStd = qVariance !== null ? Math.sqrt(qVariance) : null;

                        qStats['Average Score'].push({ x: date, y: qMean });
                        qStats['Max Score'].push({ x: date, y: qMax });
                        qStats['Min Score'].push({ x: date, y: qMin });
                        qStats['Standard Deviation'].push({ x: date, y: qStd });

                        // Compute distribution using helper function
                        const qDistribution = createDistribution(validQScores, questionStructure);
                        qStats['Distribution'].push({ x: date, y: qDistribution });
                    } else {
                        // No valid data for this date
                        qStats['Average Score'].push({ x: date, y: null });
                        qStats['Max Score'].push({ x: date, y: null });
                        qStats['Min Score'].push({ x: date, y: null });
                        qStats['Standard Deviation'].push({ x: date, y: null });
                        qStats['Distribution'].push({ x: date, y: null });
                    }
                } else {
                    // No data for this date
                    qStats['Average Score'].push({ x: date, y: null });
                    qStats['Max Score'].push({ x: date, y: null });
                    qStats['Min Score'].push({ x: date, y: null });
                    qStats['Standard Deviation'].push({ x: date, y: null });
                    qStats['Distribution'].push({ x: date, y: null });
                }
            }
        }
    }

    return output;
}

export function calculate_longitudinal_outcome_chart(
    timeSeriesData,
    anchor,
    selectedFactor,   // the outcome section the user picked
    core_data,
    outcomeQ
  ) {
    /* ---------- 1.  Map category option ids → names (unchanged) ---------- */
    const categories       = core_data.categories[0].categories;
    const anchorCategory   = categories.find((c) => c.id === anchor);
    if (!anchorCategory) throw new Error(`Anchor category with id ${anchor} not found.`);
    const categoryOptions  = Object.fromEntries(anchorCategory.options.map(o => [o.id, o.name]));
    const categoryNames    = Object.values(categoryOptions);
  
    /* ---------- 2.  Build question look-ups for the chosen outcome ---------- */
    const questionMap         = {};      // normText → { text }
    const selectedQuestionSet = new Set();
  
    outcomeQ.questions
      .filter((sec) => String(sec.id) === String(selectedFactor.id))
      .forEach((sec) => {
        sec.questions.forEach((qObj) => {
          const norm = normalise(qObj.question);      // English only
          selectedQuestionSet.add(norm);
          questionMap[norm] = { text: qObj.question };
        });
      });
  
    /* ---------- 3.  Init aggregation containers (unchanged shapes) ---------- */
    const aggregatedData = {};
    categoryNames.forEach((name) => { aggregatedData[name] = {}; });
  
    /* Helper: parse 'Q1 2024' etc. */
    const parseDateString = (d) => {
      const [q, y] = d.split(' ');
      return { year: +y, quarter: +q.replace('Q', '') };
    };
  
    const allDates = Array.from(new Set(timeSeriesData.map(tp => tp.date)))
      .sort((a, b) => {
        const A = parseDateString(a), B = parseDateString(b);
        return A.year !== B.year ? A.year - B.year : A.quarter - B.quarter;
      });
  
    /* ---------- 4.  Crunch data for every time point ---------- */
    for (const { date, responses } of timeSeriesData) {
      /* ensure containers for this date */
      categoryNames.forEach((name) => {
        aggregatedData[name][date] = aggregatedData[name][date] || {
          responses: [],
          questionResponses: {}
        };
      });
  
      responses.forEach(({ response }) => {
        /* anchor lookup */
        const catResp = response.categories.find((c) => c.id === anchor);
        if (!catResp?.response) return;
  
        const catName = categoryOptions[catResp.response];
        if (!catName) return;
  
        const entry           = aggregatedData[catName][date];
        const responsesArr    = [];
        const perQuestionTemp = {};
  
        /* employee_outcomes */
        const eo = response.employee_outcomes;
        eo?.responses?.forEach((r) => {
          const normText = normalise(r.question);
          if (!selectedQuestionSet.has(normText)) return;
  
          const val = r.response;
          if (val == null || isNaN(val)) return;
  
          responsesArr.push(val);
  
          const qText = questionMap[normText].text;
          (perQuestionTemp[qText] = perQuestionTemp[qText] || []).push(val);
        });
  
        /* aggregate */
        if (responsesArr.length) {
          const avg = safeCalculateMean(responsesArr);
          if (avg != null) entry.responses.push(avg);
  
          Object.entries(perQuestionTemp).forEach(([qTxt, vals]) => {
            (entry.questionResponses[qTxt] =
              entry.questionResponses[qTxt] || []).push(...vals);
          });
        }
      });
    }
  
    /* ---------- 5.  Build output ---------- */
    const output = { series: {}, questionSeries: {} };
  
    categoryNames.forEach((cat) => {
      /* per-category containers */
      output.series[cat] = {
        'Average Score': [], 'Max Score': [], 'Min Score': [],
        'Standard Deviation': [], 'Distribution': []
      };
      output.questionSeries[cat] = {};
      Object.values(questionMap).forEach(({ text }) => {
        output.questionSeries[cat][text] = {
          'Average Score': [], 'Max Score': [], 'Min Score': [],
          'Standard Deviation': [], 'Distribution': []
        };
      });
  
      /* walk dates oldest→newest */
      allDates.forEach((date) => {
        const entry   = aggregatedData[cat][date];
        const scores  = entry?.responses || [];
        const stats   = output.series[cat];
  
        if (scores.length) {
          const valid   = filterValidNumbers(scores);
          const mean    = safeCalculateMean(valid);
          const max     = Math.max(...valid);
          const min     = Math.min(...valid);
          const varc    = safeCalculateVariance(valid, mean);
          const std     = varc != null ? Math.sqrt(varc) : null;
          stats['Average Score'].push({ x: date, y: mean });
          stats['Max Score'].push({ x: date, y: max });
          stats['Min Score'].push({ x: date, y: min });
          stats['Standard Deviation'].push({ x: date, y: std });
          stats['Distribution'].push({ x: date, y: createDistribution(valid, core_data.questions) });
        } else {
          /* nulls keep series lengths aligned with dates */
          Object.values(stats).forEach(arr => arr.push({ x: date, y: null }));
        }
  
        /* per-question stats */
        Object.values(questionMap).forEach(({ text }) => {
          const qStats   = output.questionSeries[cat][text];
          const qVals    = entry?.questionResponses[text] || [];
          if (qVals.length) {
            const validQ  = filterValidNumbers(qVals);
            const qMean   = safeCalculateMean(validQ);
            const qMax    = Math.max(...validQ);
            const qMin    = Math.min(...validQ);
            const qVar    = safeCalculateVariance(validQ, qMean);
            const qStd    = qVar != null ? Math.sqrt(qVar) : null;
            qStats['Average Score'].push({ x: date, y: qMean });
            qStats['Max Score'].push({ x: date, y: qMax });
            qStats['Min Score'].push({ x: date, y: qMin });
            qStats['Standard Deviation'].push({ x: date, y: qStd });
            qStats['Distribution'].push({ x: date, y: createDistribution(validQ, core_data.questions) });
          } else {
            Object.values(qStats).forEach(arr => arr.push({ x: date, y: null }));
          }
        });
      });
    });
  
    return output;
  }




  export function aggregateResponsesByQuarter(dataArray,survey_version) {
    // Initialize an empty object to hold the aggregated data
    let aggregatedData = {};
    // Iterate over the dataArray
    dataArray.forEach(item => {
        if(item.question_sort_order !== survey_version.sort_order){
                return ;
        }
        // Parse the date string to get year and month
        let dateParts = item.date.split('-'); // e.g., ['2024','04','23']
        let year = dateParts[0];
        let month = parseInt(dateParts[1], 10); // Convert month to integer

        // Determine the quarter
        let quarter;
        if (month >= 1 && month <= 3) {
            quarter = 'Q1';
        } else if (month >= 4 && month <= 6) {
            quarter = 'Q2';
        } else if (month >= 7 && month <= 9) {
            quarter = 'Q3';
        } else if (month >= 10 && month <= 12) {
            quarter = 'Q4';
        } else {
            // Invalid month
            console.error('Invalid month in date:', item.date);
            return;
        }

        // Build the key 'Q{quarter} {year}'
        let key = `${quarter} ${year}`;

        // If the key doesn't exist in aggregatedData, initialize it
        if (!aggregatedData[key]) {
            aggregatedData[key] = {
                date: key,
                responses: []
            };
        }

        // Append the 'responses' array to aggregatedData[key].responses
        aggregatedData[key].responses = aggregatedData[key].responses.concat(item.responses);
    });

    // Convert the aggregatedData object to an array and return it
    return Object.values(aggregatedData);
}

